"use client";

import logger from "@/utils/logger";

import React, { useState, useCallback, createContext, useContext } from "react";
import NotificationToast from "./NotificationToast";

interface ToastData {
    id: string;
    title: string;
    message: string;
    variant?: "success" | "danger" | "warning" | "info";
    createdAt: Date;
    tripId?: number;
}

interface NotificationToastContextType {
    showNotificationToast: (toast: Omit<ToastData, "id" | "createdAt">) => void;
}

const NotificationToastContext = createContext<
    NotificationToastContextType | undefined
>(undefined);

export function useNotificationToast() {
    const ctx = useContext(NotificationToastContext);
    if (!ctx) {
        throw new Error(
            "useNotificationToast must be used within NotificationToastProvider"
        );
    }
    return ctx;
}

export function NotificationToastProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [toasts, setToasts] = useState<ToastData[]>([]);

    const showNotificationToast = useCallback(
        (toast: Omit<ToastData, "id" | "createdAt">) => {
            logger.info("🎯 showNotificationToast called with:", toast);
            const id = Math.random().toString(36).substring(2, 9);
            const newToast: ToastData = {
                ...toast,
                id,
                createdAt: new Date(),
            };

            logger.info("🎯 Adding toast to state:", newToast);
            setToasts((prev) => [...prev, newToast]);

            // Auto-dismiss after 5 seconds
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, 5000);
        },
        []
    );

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    logger.info(
        "🎯 NotificationToastProvider rendering with toasts:",
        toasts.length,
        toasts
    );

    return (
        <NotificationToastContext.Provider value={{ showNotificationToast }}>
            {children}
            <style>{`
                @keyframes slideDownFromTop {
                    from {
                        opacity: 0;
                        transform: translateY(-100%);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
            <div
                style={{
                    position: "fixed",
                    top: "0",
                    left: "0",
                    right: "0",
                    zIndex: 999999,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px",
                    paddingTop: "12px",
                    pointerEvents: "none",
                }}
            >
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        style={{
                            pointerEvents: "auto",
                            animation:
                                "slideDownFromTop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                        }}
                    >
                        <NotificationToast
                            title={toast.title}
                            message={toast.message}
                            variant={toast.variant}
                            onClose={() => removeToast(toast.id)}
                            createdAt={toast.createdAt}
                            tripId={toast.tripId}
                        />
                    </div>
                ))}
            </div>
        </NotificationToastContext.Provider>
    );
}
